/**
 * @fileoverview Case Info Step Component - Step 1 of case ingestion.
 *
 * Collects additional matter details after creation:
 * - Incident details: case type, date of loss, SOL, incident location
 * - Contact & attorney: representing attorney, contact client directly toggle
 * - Case manager: optional assignment with auto-task-assignment toggle
 * - CMS integration: external CMS URL and matter email
 *
 * This step patches the matter; it's not audit-tracked.
 * Note: incident_location is saved via incident API (not matter PATCH).
 */
import { addMonths, format, parseISO } from 'date-fns';
import { ExternalLink, FolderOpen, Hash, Mail } from 'lucide-react';
import React, { useMemo } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';

import { CmsSyncCapabilities } from '@/components/cms-sync/shared';
import { AttorneySelect } from '@/components/matter/AttorneySelect';
// TODO: Re-enable billing section when needed
// import { BILLING_GROUPS } from '@/components/matter-form/billingConfig';
import { Button } from '@/components/ui/button';
import { Checkbox, CheckboxField } from '@/components/ui/catalyst/checkbox';
import { ErrorMessage, Field, Label } from '@/components/ui/catalyst/fieldset';
import { Input } from '@/components/ui/catalyst/input';
import { Textarea } from '@/components/ui/catalyst/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useGetFirmCaseManagersQuery } from '@/services/api/caseManagerService';
import {
  getCmsTargetSystemFromUrl,
  getDriveSystemFromUrl,
} from '@/utils/externalSystemUtils';

import type { CreateMatterFormValues } from '../../types';
import { handleDatePaste } from '../../utils';

interface CaseInfoStepProps {
  /** React Hook Form control */
  control: Control<CreateMatterFormValues>;
  /** Form validation errors */
  errors: FieldErrors<CreateMatterFormValues>;
  /** Selected firm ID (for fetching case managers) */
  firmId: string | null;
  /** React Hook Form setValue function */
  setValue: UseFormSetValue<CreateMatterFormValues>;
  /** Whether form is in loading state */
  isLoading: boolean;
}

/** Reusable section wrapper with title header. */
const SectionGroup: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <div className="space-y-3">
    <h3 className="text-xs font-semibold text-zinc-900 dark:text-white">
      {title}
    </h3>
    {children}
  </div>
);

export const CaseInfoStep: React.FC<CaseInfoStepProps> = ({
  control,
  errors,
  firmId,
  setValue,
  isLoading,
}) => {
  // TODO: Re-enable billing section when needed
  // Watch billing code for selection state
  // const selectedBillingCode = useWatch({
  //   control,
  //   name: 'simplified_billing_code',
  // });

  // Watch case manager for auto-assign toggle
  const selectedCaseManagerId = useWatch({
    control,
    name: 'case_manager_id',
  });

  // Watch CMS fields for button state
  const currentExternalCmsUrl = useWatch({
    control,
    name: 'external_cms_url',
  });

  const currentExternalDriveUrl = useWatch({
    control,
    name: 'external_drive_url',
  });

  const currentCmsEmail = useWatch({
    control,
    name: 'cms_email',
  });

  // Watch DOL for SOL preset calculation
  const dateOfLoss = useWatch({ control, name: 'date_of_loss' });

  // Fetch case managers if firm is selected
  const { data: caseManagers = [], isLoading: isLoadingCaseManagers } =
    useGetFirmCaseManagersQuery({ firmId: firmId || '' }, { skip: !firmId });

  const hasCaseManagerSelected =
    selectedCaseManagerId && selectedCaseManagerId !== 'none';

  // TODO: Re-enable billing section when needed
  // const handleBillingCodeClick = useCallback(
  //   (code: SimplifiedBillingCode) => {
  //     if (selectedBillingCode === code) {
  //       setValue('simplified_billing_code', null, { shouldValidate: true });
  //     } else {
  //       setValue('simplified_billing_code', code, { shouldValidate: true });
  //     }
  //   },
  //   [selectedBillingCode, setValue],
  // );

  const displayUrl = useMemo(() => {
    try {
      return new URL(currentExternalCmsUrl ?? '').toString();
    } catch {
      return null;
    }
  }, [currentExternalCmsUrl]);

  const displayDriveUrl = useMemo(() => {
    try {
      return new URL(currentExternalDriveUrl ?? '').toString();
    } catch {
      return null;
    }
  }, [currentExternalDriveUrl]);

  const detectedCms = useMemo(
    () => getCmsTargetSystemFromUrl(currentExternalCmsUrl),
    [currentExternalCmsUrl],
  );

  const detectedDrive = useMemo(
    () => getDriveSystemFromUrl(currentExternalDriveUrl),
    [currentExternalDriveUrl],
  );
  const detectedSystem = detectedCms ?? detectedDrive;
  const driveLabelMap = {
    DROPBOX: 'Dropbox Folder URL',
    GOOGLE_DRIVE: 'Google Drive Folder URL',
  } as const;
  const driveOpenLabelMap = {
    DROPBOX: 'Dropbox',
    GOOGLE_DRIVE: 'Google Drive',
  } as const;
  const driveLabel = detectedDrive ? driveLabelMap[detectedDrive] : 'Drive Folder URL';
  const drivePlaceholder =
    detectedDrive === 'DROPBOX'
      ? 'https://www.dropbox.com/home/...'
      : 'https://drive.google.com/drive/folders/... or https://www.dropbox.com/home/...';
  const driveOpenLabel = detectedDrive
    ? driveOpenLabelMap[detectedDrive]
    : 'external drive';

  return (
    <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 divide-y divide-zinc-200 dark:divide-zinc-800 overflow-hidden">
      {/* Incident Details */}
      <div className="px-3 py-3">
        <SectionGroup title="Incident Details">
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field>
                <Label htmlFor="date_of_loss">Date of Loss</Label>
                <Controller
                  name="date_of_loss"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="date_of_loss"
                      type="date"
                      data-slot="control"
                      disabled={isLoading}
                      {...field}
                      value={field.value ?? ''}
                      onPaste={(e) => handleDatePaste(e, field.onChange)}
                    />
                  )}
                />
                {errors.date_of_loss?.message && (
                  <ErrorMessage>{errors.date_of_loss.message}</ErrorMessage>
                )}
              </Field>

              <Field>
                <Label htmlFor="statute_of_limitations_date">
                  Statute of Limitations
                </Label>
                <Controller
                  name="statute_of_limitations_date"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="statute_of_limitations_date"
                      type="date"
                      data-slot="control"
                      disabled={isLoading}
                      {...field}
                      value={field.value ?? ''}
                      onPaste={(e) => handleDatePaste(e, field.onChange)}
                    />
                  )}
                />
                <div className="flex items-center gap-2 mt-1">
                  {[12, 18, 24].map((months) => (
                    <button
                      key={months}
                      type="button"
                      disabled={!dateOfLoss || isLoading}
                      className="text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:underline disabled:text-zinc-400 disabled:no-underline disabled:cursor-not-allowed"
                      onClick={() => {
                        if (!dateOfLoss) return;
                        const sol = addMonths(parseISO(dateOfLoss), months);
                        setValue(
                          'statute_of_limitations_date',
                          format(sol, 'yyyy-MM-dd'),
                        );
                      }}
                    >
                      +{months}mo
                    </button>
                  ))}
                  {!dateOfLoss && (
                    <span className="text-xs text-zinc-500">
                      Enter DOL first
                    </span>
                  )}
                </div>
                {errors.statute_of_limitations_date?.message && (
                  <ErrorMessage>
                    {errors.statute_of_limitations_date.message}
                  </ErrorMessage>
                )}
              </Field>
            </div>

            <Field>
              <Label htmlFor="incident_location">Incident Location</Label>
              <Controller
                name="incident_location"
                control={control}
                render={({ field }) => (
                  <Textarea
                    id="incident_location"
                    data-slot="control"
                    disabled={isLoading}
                    rows={2}
                    className="resize-y"
                    placeholder="e.g., 123 Main St, Los Angeles, CA or intersection of 1st and Main"
                    {...field}
                    value={field.value ?? ''}
                  />
                )}
              />
              {errors.incident_location?.message && (
                <ErrorMessage>{errors.incident_location.message}</ErrorMessage>
              )}
            </Field>

            <Field>
              <Label htmlFor="incident_summary">Incident Summary</Label>
              <Controller
                name="incident_summary"
                control={control}
                render={({ field }) => (
                  <Textarea
                    id="incident_summary"
                    data-slot="control"
                    disabled={isLoading}
                    rows={4}
                    className="resize-y"
                    placeholder="Brief description of the accident/incident"
                    {...field}
                    value={field.value ?? ''}
                  />
                )}
              />
              {errors.incident_summary?.message && (
                <ErrorMessage>{errors.incident_summary.message}</ErrorMessage>
              )}
            </Field>
          </div>
        </SectionGroup>
      </div>

      {/* Contact & Attorney */}
      <div className="px-3 py-3">
        <SectionGroup title="Contact & Attorney">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field>
              <Label htmlFor="representing_attorney">Attorney</Label>
              <Controller
                name="representing_attorney"
                control={control}
                render={({ field }) => (
                  <AttorneySelect
                    id="representing_attorney"
                    firmId={firmId || ''}
                    value={field.value ?? null}
                    onChange={(val) => field.onChange(val)}
                    placeholder="Select attorney"
                  />
                )}
              />
            </Field>

            <Field>
              <Label htmlFor="contact_client_directly">
                Contact Client Directly
              </Label>
              <Controller
                name="contact_client_directly"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Select
                    value={value ? 'yes' : 'no'}
                    onValueChange={(v) => onChange(v === 'yes')}
                    disabled={isLoading}
                  >
                    <SelectTrigger id="contact_client_directly">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">
                        Yes - Finch may contact client
                      </SelectItem>
                      <SelectItem value="no">
                        No - Only firm contacts client
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </Field>
          </div>
        </SectionGroup>
      </div>

      {/* TODO: Re-enable billing section when needed */}
      {/* <div className="px-3 py-3">
        <SectionGroup title="Billing">
          <div className="space-y-3">
            {BILLING_GROUPS.map((section) => (
              <div
                key={section.title}
                className="rounded-lg border border-zinc-200 dark:border-zinc-800 overflow-hidden"
              >
                <div className="px-3 py-1.5 bg-zinc-50 dark:bg-zinc-800/50 border-b border-zinc-200 dark:border-zinc-800">
                  <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                    {section.title}
                  </span>
                </div>
                <div className="bg-white dark:bg-zinc-900">
                  {section.options.map((option) => (
                    <button
                      key={option.code}
                      type="button"
                      disabled={isLoading}
                      onClick={() => handleBillingCodeClick(option.code)}
                      className={cn(
                        'flex items-center gap-3 w-full px-3 py-1.5 text-left transition-colors',
                        'border-b border-zinc-100 dark:border-zinc-800 last:border-b-0',
                        selectedBillingCode === option.code
                          ? 'bg-indigo-50 dark:bg-indigo-950/30'
                          : 'hover:bg-zinc-50 dark:hover:bg-zinc-800/50',
                        isLoading && 'opacity-50 cursor-not-allowed',
                      )}
                    >
                      <div
                        className={cn(
                          'h-4 w-4 rounded-full border-2 flex-shrink-0 transition-colors',
                          selectedBillingCode === option.code
                            ? 'border-indigo-600 bg-indigo-600'
                            : 'border-zinc-300 dark:border-zinc-600',
                        )}
                      >
                        {selectedBillingCode === option.code && (
                          <div className="h-full w-full flex items-center justify-center">
                            <div className="h-1.5 w-1.5 rounded-full bg-white" />
                          </div>
                        )}
                      </div>
                      <span className="text-sm font-medium text-zinc-900 dark:text-white min-w-32">
                        {option.label}
                      </span>
                      <div className="flex flex-col">
                        <span className="text-xs text-zinc-500 dark:text-zinc-400">
                          {option.description}
                        </span>
                        {option.note && (
                          <span className="mt-1 inline-flex self-start text-[10px] font-medium text-zinc-500 dark:text-zinc-400 bg-zinc-100 dark:bg-zinc-800 px-1.5 py-0.5 rounded">
                            {option.note}
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </SectionGroup>
      </div> */}

      {/* Case Manager */}
      {firmId && (
        <div className="px-3 py-3">
          <SectionGroup title="Case Manager">
            {caseManagers.length > 0 ? (
              <div className="space-y-3">
                <Controller
                  name="case_manager_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value || 'none'}
                      onValueChange={(value) =>
                        field.onChange(value === 'none' ? null : value)
                      }
                      disabled={isLoading || isLoadingCaseManagers}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="None (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">
                          <span className="text-zinc-500 dark:text-zinc-400">
                            None
                          </span>
                        </SelectItem>
                        {caseManagers.map((cm) => (
                          <SelectItem key={cm.id} value={cm.id}>
                            {cm.display_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />

                {hasCaseManagerSelected && (
                  <Controller
                    name="auto_task_assignment"
                    control={control}
                    render={({ field }) => (
                      <CheckboxField>
                        <Checkbox
                          checked={field.value ?? true}
                          onChange={(checked) =>
                            field.onChange(Boolean(checked))
                          }
                          disabled={isLoading}
                          color="indigo"
                        />
                        <Label className="text-sm cursor-pointer">
                          Auto-assign tasks
                        </Label>
                      </CheckboxField>
                    )}
                  />
                )}
                {hasCaseManagerSelected && (
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 ml-7 -mt-1">
                    Tasks go to the case manager
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-zinc-500 dark:text-zinc-400">
                No case managers configured for this firm.{' '}
                <a
                  href={`/firms/${firmId}/role-assignments`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:text-indigo-800 hover:underline"
                >
                  Configure case managers
                </a>
              </p>
            )}
          </SectionGroup>
        </div>
      )}

      {/* CMS Integration */}
      <div className="px-3 py-3">
        <SectionGroup title="CMS Integration">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field>
              <Label htmlFor="external_cms_url">External CMS URL</Label>
              <Controller
                control={control}
                name="external_cms_url"
                render={({ field, fieldState }) => (
                  <>
                    <div className="flex gap-2">
                      <Input
                        {...field}
                        id="external_cms_url"
                        data-slot="control"
                        placeholder="https://..."
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value || null)}
                        className={fieldState.error ? 'border-destructive' : ''}
                      />
                      {displayUrl && !fieldState.error && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => window.open(displayUrl, '_blank')}
                          title="Open in external CMS"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    {fieldState.error && (
                      <p className="text-sm text-destructive">
                        {fieldState.error.message || 'Please enter a valid URL'}
                      </p>
                    )}
                  </>
                )}
              />
            </Field>

            <Field>
              <Label htmlFor="external_drive_url">{driveLabel}</Label>
              <Controller
                control={control}
                name="external_drive_url"
                render={({ field, fieldState }) => (
                  <>
                    <div className="flex gap-2">
                      <Input
                        {...field}
                        id="external_drive_url"
                        data-slot="control"
                        placeholder={drivePlaceholder}
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value || null)}
                        className={fieldState.error ? 'border-destructive' : ''}
                      />
                      {displayDriveUrl && !fieldState.error && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => window.open(displayDriveUrl, '_blank')}
                          title={`Open in ${driveOpenLabel}`}
                        >
                          <FolderOpen className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    {fieldState.error && (
                      <p className="text-sm text-destructive">
                        {fieldState.error.message || 'Please enter a valid URL'}
                      </p>
                    )}
                  </>
                )}
              />
            </Field>

            <Field>
              <Label htmlFor="cms_email">Matter Email</Label>
              <Controller
                control={control}
                name="cms_email"
                render={({ field, fieldState }) => (
                  <>
                    <div className="flex gap-2">
                      <Input
                        {...field}
                        id="cms_email"
                        type="email"
                        data-slot="control"
                        placeholder="e.g., matter-12345@firm.filevine.io"
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value || null)}
                        className={fieldState.error ? 'border-destructive' : ''}
                      />
                      {currentCmsEmail && !fieldState.error && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() =>
                            window.open(`mailto:${currentCmsEmail}`, '_blank')
                          }
                          title="Compose email to this address"
                        >
                          <Mail className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    {fieldState.error && (
                      <ErrorMessage>
                        {fieldState.error.message ||
                          'Please enter a valid email'}
                      </ErrorMessage>
                    )}
                  </>
                )}
              />
            </Field>
          </div>

          <Field>
            <Label htmlFor="case_id">Case ID</Label>
            <Controller
              control={control}
              name="case_id"
              render={({ field }) => (
                <div className="flex gap-2">
                  <Input
                    {...field}
                    id="case_id"
                    data-slot="control"
                    placeholder="e.g., 2737337"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value || null)}
                  />
                  {field.value && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      title="Case ID"
                      disabled
                    >
                      <Hash className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              )}
            />
          </Field>

          <CmsSyncCapabilities detectedCms={detectedSystem} />
        </SectionGroup>
      </div>
    </div>
  );
};
